import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Animated,
  Dimensions,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import ImageCarousel from './ImageCarousel';
import ProLock from '../common/ProLock';

const ProductData = ({
  name,
  model,
  description,
  styleMatch = 0,
  hideStyleMatch = false,
  images = [],
  mode = 'live',
  onFindMyCar = () => {},
  // Report generation props
  modelId,
  existingReport,
  session,
  onReportPress,
  // Notification props
  showSuccessNotification = false,
  onNotificationDismiss = () => {},
  // Image fallback props
  placeholderImageUrl,
  imageFidelity,
}) => {
  // Track screen width for responsive layout
  const [screenWidth, setScreenWidth] = useState(Dimensions.get('window').width);
  const BREAKPOINT_WIDTH = 768;

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenWidth(window.width);
    });
    return () => subscription?.remove();
  }, []);

  const isMobile = screenWidth < BREAKPOINT_WIDTH;
  const posthog = usePostHog();
  // Animation for notification
  const fadeAnim = useRef(new Animated.Value(0)).current;

  // Handle notification auto-dismiss
  useEffect(() => {
    if (showSuccessNotification) {
      // Fade in
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
      }).start();

      // Auto-dismiss after 60 seconds
      const timer = setTimeout(() => {
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 300,
          useNativeDriver: true,
        }).start(() => {
          onNotificationDismiss();
        });
      }, 60000);

      return () => clearTimeout(timer);
    }
  }, [showSuccessNotification, fadeAnim, onNotificationDismiss]);

  // Determine button state for report generation
  const reportStatus = existingReport?.status;
  const isGenerating = [
    'pending',
    'generating_content',
    'content_complete',
    'generating_pdf',
  ].includes(reportStatus);
  // Only consider complete when status is 'complete' AND pdf_url exists
  const isComplete = reportStatus === 'complete' && existingReport?.pdf_url;
  const isFailed = reportStatus === 'failed';

  // Button text and handler based on mode and report status
  let buttonText = 'Download full report';
  let buttonHandler = () => {};
  let buttonDisabled = false;
  let showLoader = false;

  if (mode === 'shared') {
    // Shared mode: Always show "Find my car"
    buttonText = 'Find my car';
    buttonHandler = onFindMyCar;
  } else {
    // Live mode: Handle report states
    if (isComplete) {
      buttonText = 'View Report';
      buttonHandler = onReportPress;
    } else if (isGenerating) {
      buttonText = 'Generating Report...';
      buttonHandler = () => {};
      buttonDisabled = true;
      showLoader = true;
    } else if (!existingReport || isFailed) {
      buttonText = 'Get Full Report';
      buttonHandler = onReportPress;
    }
  }

  return (
    <View style={[styles.container, isMobile && styles.containerMobile]}>
      <View style={[styles.textSection, isMobile && styles.textSectionMobile]}>
        <View style={[styles.headerSection, isMobile && styles.headerSectionMobile]}>
          {!hideStyleMatch && <Text style={styles.styleMatch}>{styleMatch}% YOUR STYLE</Text>}
          <View style={styles.titleSection}>
            <Text style={styles.vehicleName}>{name}</Text>
            <Text style={styles.modelYear}>{model}</Text>
          </View>
        </View>
        <Text style={[styles.description, isMobile && styles.descriptionMobile]}>
          {description}
        </Text>
        <View style={[styles.buttonGroup, isMobile && styles.buttonGroupMobile]}>
          <ProLock mode="badge" feature="pdf_report">
            <TouchableOpacity
              style={[styles.primaryButton, buttonDisabled && styles.primaryButtonDisabled]}
              onPress={buttonHandler}
              disabled={buttonDisabled}
              testID="generate-report-button"
            >
              {showLoader && (
                <ActivityIndicator size="small" color="#FFFFFF" style={styles.buttonLoader} />
              )}
              <Text style={styles.primaryButtonText}>{buttonText}</Text>
            </TouchableOpacity>
          </ProLock>
        </View>

        {/* Success notification */}
        {showSuccessNotification && (
          <Animated.View style={[styles.notification, { opacity: fadeAnim }]}>
            <Icon name="check-circle" size={18} color="#144CB8" style={styles.notificationIcon} />
            <Text style={styles.notificationText}>
              Report is being generated. Feel free to browse other recommendations - we&apos;ll
              email you when ready!
            </Text>
          </Animated.View>
        )}
      </View>
      <View style={[styles.carouselWrapper, isMobile && styles.carouselWrapperMobile]}>
        <ImageCarousel
          images={images}
          placeholderImageUrl={placeholderImageUrl}
          imageFidelity={imageFidelity}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 16,
  },
  containerMobile: {
    flexDirection: 'column',
    gap: 24,
  },
  carouselWrapper: {
    flex: 1,
  },
  carouselWrapperMobile: {
    flex: 0,
    height: 300,
    minHeight: 300,
  },
  textSection: {
    flex: 1,
    flexDirection: 'column',
    paddingLeft: 80,
    paddingBottom: 80,
  },
  textSectionMobile: {
    paddingLeft: 0,
    paddingBottom: 24,
  },
  headerSection: {
    flexDirection: 'column',
    gap: 5,
    marginBottom: 48,
  },
  headerSectionMobile: {
    marginBottom: 24,
  },
  styleMatch: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 14,
    color: '#144CB8',
    textTransform: 'uppercase',
  },
  titleSection: {
    gap: 8,
  },
  vehicleName: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 32,
    color: '#FFFFFF',
  },
  modelYear: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 18,
    color: 'rgba(255, 255, 255, 0.6)',
  },
  description: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.7)',
    marginBottom: 48,
  },
  descriptionMobile: {
    marginBottom: 24,
  },
  buttonGroup: {
    flexDirection: 'row',
    gap: 14,
  },
  buttonGroupMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 12,
  },
  primaryButton: {
    backgroundColor: '#144CB8',
    paddingVertical: 12,
    paddingHorizontal: 32,
    borderRadius: 32,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  primaryButtonDisabled: {
    backgroundColor: '#8BA3D1',
    opacity: 0.7,
    ...Platform.select({
      web: {
        cursor: 'not-allowed',
      },
    }),
  },
  primaryButtonText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 14,
    color: '#FFFFFF',
    letterSpacing: -0.14,
  },
  buttonLoader: {
    marginRight: 4,
  },
  notification: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: 'rgba(5, 86, 247, 0.12)',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginTop: 16,
    gap: 10,
    borderLeftWidth: 3,
    borderLeftColor: '#0556F7',
  },
  notificationIcon: {
    marginTop: 2,
  },
  notificationText: {
    flex: 1,
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    lineHeight: 20,
    color: '#144CB8',
  },
});

export default ProductData;
